import Typography from "@material-ui/core/Typography";
import {
  Activity,
  ActivityFeedItemDefinition,
} from "@ofqual/portal-activity-feed";
import { LcmModel } from "../../domain/LcmModel";
import { ActivityType } from "./ActivityType";
import {
  CaseNoteActivity,
  ChangeActivityBody,
  DialogType,
  EmailActivityBody,
  ExternalCounselActivityBodyProps,
  FutureEventActivity,
  InternalMessage,
  TimeRecordLoggedActivityBody,
} from "./types";
import ReactMarkdown from "react-markdown";
import { Chip, Grid } from "@material-ui/core";
import { fieldLabels } from "../sections/fieldlabels";
import { UserRole } from "../../redux/types";
import {
  requestExternalCounselAccept,
  requestExternalCounselReject,
} from "../../redux/actions";
import { Dispatch } from "react";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { PreviewDocument } from "./PreviewDocument";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import { lcmService } from "../../services";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import { CommunicationType } from "../../domain/FutureEventModel";

export const buildDefinitions = (
  setFocusedActivity: (activity: Activity, activityType?: DialogType) => void,
  lcmItem: LcmModel,
  roles: number[],
  dispatch: Dispatch<any>,
  upn: string,
  counselCategoryLookups: ReturnType<typeof useLookup>[1],
  caseNoteLookups: ReturnType<typeof useLookup>[2],
  projectPolicyLookups: ReturnType<typeof useLookup>[3],
  setShowConfirm: (show: boolean) => void,
  setShowConfirmFunc: (func: any) => void,
  setConfirmText: React.Dispatch<React.SetStateAction<string>>
) => {
  const internalMessageAdded: ActivityFeedItemDefinition<InternalMessage> = {
    activityType: ActivityType.LcmInternalMessage,
    Body: ({ body, activity }) => (
      <>
        {body.message ? (
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>Message: </b>
            <ReactMarkdown>{body.message}</ReactMarkdown>
            <b>Recipients: </b>
            <>
              {body.recipients.map((r) => (
                <Chip label={r.name} color="secondary" />
              ))}
            </>
            <br />
            <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
              {body.documents?.map((doc) => (
                <Grid key={doc.blobId} container spacing={1}>
                  {
                    <Grid item xs={12}>
                      <PreviewDocument
                        doc={doc}
                        lcmItem={lcmItem}
                        activity={activity}
                      />
                    </Grid>
                  }
                </Grid>
              ))}
            </ul>
          </div>
        ) : null}
      </>
    ),
  };

  const getExternalCounselRequestActions = (activityId: string) => {
    const rejectFunc = () => {
      dispatch(requestExternalCounselReject(lcmItem.id, activityId));
    };

    const approveFunc = () => {
      dispatch(requestExternalCounselAccept(lcmItem.id, activityId));
    };

    return [
      {
        dataTest: "rec-approve-button",
        label: "Approve",
        onClick: () => {
          setConfirmText("Confirm approval of external counsel resource");
          setShowConfirm(true);
          setShowConfirmFunc(() => approveFunc);
        },
      },
      {
        label: "Reject",
        dataTest: "rec-reject-button",
        onClick: () => {
          setConfirmText("Confirm reject of external counsel resource");
          setShowConfirm(true);
          setShowConfirmFunc(() => rejectFunc);
        },
      },
    ];
  };

  const lcmExternalCounselRequested: ActivityFeedItemDefinition<ExternalCounselActivityBodyProps> =
    {
      activityType: ActivityType.LcmExternalCounselRequest,
      Body: ({ body }) => {
        return (
          <>
            <div>
              <b>{fieldLabels.requestExternalCounselName}: </b>
              <text data-test={"rec-activity-name"}>{body.name}</text>
            </div>
            <div>
              <b>{fieldLabels.email}: </b>
              <text data-test={"rec-activity-email"}>{body.email}</text>
            </div>
            <div>
              <b>{fieldLabels.category}: </b>
              <text data-test={"rec-activity-counsel-category"}>
                {counselCategoryLookups.find((x) => x.id === body.category)
                  ?.label || ""}
              </text>
            </div>
            <div>
              <b>{fieldLabels.note}: </b>
              <text data-test={"rec-activity-counsel-note"}>
                <ReactMarkdown>
                  {body.notes === null ? "N/A" : body.notes}
                </ReactMarkdown>
              </text>
            </div>
            <div>
              <b>{fieldLabels.forecast}: £ </b>
              <text data-test={"rec-activity-counsel-forecast"}>
                {body.forecast}
              </text>
            </div>
          </>
        );
      },
      dynamicOptions: (props) =>
        (roles.includes(UserRole.LPM) ||
          roles.includes(UserRole.Director) ||
          roles.includes(UserRole.GC)) &&
        props.body.status === 0
          ? getExternalCounselRequestActions(props.activity.id)
          : [],
    };

  const timeRecordLogged: ActivityFeedItemDefinition<TimeRecordLoggedActivityBody> =
    {
      activityType: ActivityType.LcmTimeLog,
      Body: ({ body }) => {
        return (
          <>
            <div>
              <b>{fieldLabels.name}: </b>
              <text data-test={"add-time-name-activity-field"}>
                {body.name}
              </text>
            </div>
            <div>
              <b>{fieldLabels.externalCounsel}: </b>
              <text data-test={"add-time-external-counsel-activity-field"}>
                {body.externalCounsel}
              </text>
            </div>
            <div>
              <b>{fieldLabels.note}: </b>
              <text data-test={"add-time-notes-activity-field"}>
                <ReactMarkdown>
                  {body.notes === null ? "N/A" : body.notes}
                </ReactMarkdown>
              </text>
            </div>
            <div>
              <b>{fieldLabels.duration}: </b>
              <text data-test={"add-time-duration-activity-field"}>
                <>
                  {Math.floor(body.duration / (24 * 60 * 60 * 10000000))} days,
                </>
                &nbsp;
                <>
                  {Math.floor(body.duration / (60 * 60 * 10000000)) % 24} hours,
                </>
                &nbsp;
                <>{Math.floor(body.duration / (60 * 10000000)) % 60} minutes</>
              </text>
            </div>
            <div>
              <b>{fieldLabels.forecast}: £ </b>
              <text data-test={"add-time-forecast-activity-field"}>
                {body.forecast}
              </text>
            </div>
            <div>
              <b>{fieldLabels.dateAssigned}: </b>
              <>{new Date(body.requestDate).toLocaleDateString()}</>
            </div>
          </>
        );
      },
    };

  const caseNoteActivity: ActivityFeedItemDefinition<CaseNoteActivity> = {
    activityType: ActivityType.LcmCaseNote,
    Body: ({ body, activity }) => {
      return (
        <>
          <div>
            <b>{fieldLabels.caseNoteType}: </b>
            <text data-test={"add-note-type-activity-field"}>
              {caseNoteLookups.find((x) => x.id === body.noteType.toString())
                ?.label || ""}
            </text>
          </div>
          <div style={{ marginBottom: 15 }}>
            <b>Note: </b>
            <text data-test={"add-note-note-activity-field"}>
              <ReactMarkdown>{body.note}</ReactMarkdown>
            </text>
          </div>
          <div>
            <b>{fieldLabels.date}: </b>
            <text data-test={"add-note-meeting-date-activity-field"}>
              {new Date(body.noteDate).toLocaleDateString()}
            </text>
          </div>
          <div>
            <b>{fieldLabels.name}: </b>
            <text data-test={"add-note-name-activity-field"}>
              {body.notedByName}
            </text>
          </div>
          <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
            {body.documents?.map((doc) => (
              <Grid key={doc.blobId} container spacing={1}>
                {
                  <Grid item xs={12}>
                    <PreviewDocument
                      doc={doc}
                      lcmItem={lcmItem}
                      activity={activity}
                    />
                  </Grid>
                }
              </Grid>
            ))}
          </ul>
        </>
      );
    },
  };

  const emailAdded: ActivityFeedItemDefinition<EmailActivityBody> = {
    activityType: ActivityType.LcmEmail,
    Body: ({ body, activity }) => {
      const { token } = useSelector(authSelector);
      const buildGetBlob = (blobId: string) => () =>
        lcmService.downloadEmailAttachment(lcmItem.id, activity.id, blobId, {
          token,
        });
      return (
        <>
          <div style={{ marginBottom: 15 }}>
            <b>Subject: </b>
            <Typography>{body.subject}</Typography>
            <b>From: </b>
            <Typography>{body.from}</Typography>
            <b>To: </b>
            <Typography>{body.to.join(", ")}</Typography>
            <b>Preview: </b>
            <Typography>{body.bodyText?.substring(0, 200)}</Typography>
            {(body.attachments?.length > 0 ?? false) && (
              <>
                <b>Attachments: </b>
                <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
                  {body.attachments.map((doc) => (
                    <li key={doc.blobId} style={{ listStyleType: "none" }}>
                      {
                        <DownloadLink
                          fileName={doc.fileName}
                          getBlob={buildGetBlob(doc.blobId)}
                        />
                      }
                    </li>
                  ))}
                </ul>
              </>
            )}
            <b>Download original email: </b>
            <DownloadLink
              fileName={body.originalFileName}
              getBlob={buildGetBlob(body.originalEmailBlobId)}
            />
          </div>
        </>
      );
    },
  };

  const futureEventAdded: ActivityFeedItemDefinition<FutureEventActivity> = {
    activityType: ActivityType.LcmFutureEvent,
    Body: ({ body }) => {
      const dateTimeOptions: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      };
      const convertedDateTime = new Date(body.dateTime).toLocaleString(
        "en-GB",
        dateTimeOptions
      );
      return (
        <>
          {body.dateTime ? (
            <div style={{ marginBottom: 15 }}>
              <b>Date and time: </b>
              <Typography data-test={"future-event-date-time-activity-item"}>
                {convertedDateTime}
              </Typography>
              <br />
              <b>Event type: </b>
              <Typography data-test={"future-event-event-type-activity-item"}>
                {body.eventType}
              </Typography>
              <br />
              <b>Means of communication: </b>
              <Typography
                data-test={"future-event-means-of-communication-activity-item"}
              >
                {CommunicationType[body.communicationType]}
              </Typography>
              <br />
              <b>Notes: </b>
              {/* <ReactMarkdown data-test={"future-event-notes-activity-item"}>{body.note}</ReactMarkdown> */}
              <Typography data-test={"future-event-notes-activity-item"}>
                {body.note}
              </Typography>
            </div>
          ) : null}
        </>
      );
    },
  };

  const lcmUpdated: ActivityFeedItemDefinition<ChangeActivityBody> = {
    activityType: ActivityType.LcmChanged,
    Body: ({ body }) => (
      <>
        <b>Updated:</b>{" "}
        {body.changes.map((item) => (
          <Chip
            size="small"
            style={{ margin: 2, display: "flex-grid" }}
            label={fieldLabels[item.field]}
            className="ChangeChip"
            key={item.field}
          />
        ))}
      </>
    ),
    options: [
      {
        label: "Show Changes",
        // onClick: setFocusedActivity(ActivityType,activityType = DialogType.LcmChanged),
        // onClick={() => setFocusedEntity({dialogType: DialogType.LcmChanged})}
        onClick: (x) => {
          setFocusedActivity(x, DialogType.LcmChanged);
        },
      },
    ],
  };

  return [
    internalMessageAdded,
    lcmExternalCounselRequested,
    timeRecordLogged,
    caseNoteActivity,
    emailAdded,
    futureEventAdded,
    lcmUpdated,
  ];
};
